// Mantis color palette
import { alpha } from '@mui/material/styles';

export interface ColorProps {
  readonly lighter: string;
  readonly light: string;
  readonly main: string;
  readonly dark: string;
  readonly darker: string;
  readonly contrastText: string;
}

// Primary color - Blue
const PRIMARY: ColorProps = {
  lighter: '#C8FACD',
  light: '#5BE584',
  main: '#1890ff',
  dark: '#007B55',
  darker: '#005249',
  contrastText: '#fff'
};

// Secondary color - Purple
const SECONDARY: ColorProps = {
  lighter: '#D6E4FF',
  light: '#84A9FF',
  main: '#3366FF',
  dark: '#1939B7',
  darker: '#091A7A',
  contrastText: '#fff'
};

// Success color - Green
const SUCCESS: ColorProps = {
  lighter: '#D8FBDE',
  light: '#86E8AB',
  main: '#36B37E',
  dark: '#1B806A',
  darker: '#0A5554',
  contrastText: '#fff'
};

// Warning color - Orange
const WARNING: ColorProps = {
  lighter: '#FFF5CC',
  light: '#FFD666',
  main: '#FFAB00',
  dark: '#B76E00',
  darker: '#7A4100',
  contrastText: '#fff'
};

// Error color - Red
const ERROR: ColorProps = {
  lighter: '#FFE9D5',
  light: '#FFAC82',
  main: '#FF5630',
  dark: '#B71D18',
  darker: '#7A0916',
  contrastText: '#fff'
};

// Info color - Cyan
const INFO: ColorProps = {
  lighter: '#CAFDF5',
  light: '#61F3F3',
  main: '#00B8D9',
  dark: '#006C9C',
  darker: '#003768',
  contrastText: '#fff'
};

// Grey colors
const GREY = {
  0: '#FFFFFF',
  50: '#FAFAFA',
  100: '#F5F5F5',
  200: '#EEEEEE',
  300: '#E0E0E0',
  400: '#BDBDBD',
  500: '#9E9E9E',
  600: '#757575',
  700: '#616161',
  800: '#424242',
  900: '#212121'
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  divider: alpha(GREY[500], 0.24),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48
  }
};

export default function Palette(mode: 'light' | 'dark') {
  const light = {
    ...COMMON,
    mode: 'light' as const,
    text: {
      primary: GREY[800],
      secondary: GREY[600],
      disabled: GREY[500]
    },
    background: {
      paper: '#fff',
      default: GREY[100]
    },
    action: {
      ...COMMON.action,
      active: GREY[600]
    }
  };

  const dark = {
    ...COMMON,
    mode: 'dark' as const,
    text: {
      primary: '#fff',
      secondary: GREY[500],
      disabled: GREY[600]
    },
    background: {
      paper: GREY[800],
      default: GREY[900]
    },
    action: {
      ...COMMON.action,
      active: GREY[500]
    }
  };

  return mode === 'light' ? light : dark;
}
